import { useRouter } from "next/router";
import { useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { data as appData } from "shared/data";
import { useCanonicalUrl } from "shared/hooks";

import { AppFooter } from "scmp-app/components/app-footer";
import { AuthorList } from "scmp-app/components/author/author-list";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { LetterList } from "scmp-app/components/letter-list";
import { getLetterFromQueryString } from "scmp-app/components/letter-list/helpers";
import { PageSeo } from "scmp-app/components/meta";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { page } from "scmp-app/pages/authors";
import type {
  authorsPageQuery,
  StringFilterOperator,
} from "scmp-app/queries/__generated__/authorsPageQuery.graphql";

import { MainHeading, PageContainer, PageContent, SubHeading } from "./styles";

const pageQuery = graphql`
  query authorsPageQuery(
    $name: String!
    $nameOperator: StringFilterOperator = START_WITH
    $types: [String] = []
    $typesOperator: ArrayFilterOperator = NOT_CONTAIN
    $published: Boolean = true
    $first: Int
    $after: String
    $applicationId: String!
  ) {
    ...authorListQuery
  }
`;

const AuthorsPage: RelayComponent<unknown, authorsPageQuery> = ({ preloadedQuery }) => {
  const { pathname, query } = useRouter();
  const queryReference = usePreloadedQuery(pageQuery, preloadedQuery);
  const selectedLetter = getLetterFromQueryString(query.char);
  const handleGetPageRoute = useMemo(() => page.route, []);

  // SEO enhancement, canonicalizing all /authors?char=... pages to /authors
  const { canonicalUrl } = useCanonicalUrl(pathname);

  return (
    <>
      <PageContainer>
        <PageContent>
          <PageSeo canonical={canonicalUrl} title="All Authors" />
          <MainHeading>Authors</MainHeading>
          <LetterList route={handleGetPageRoute} />
          <SubHeading>Author - {selectedLetter}</SubHeading>
          <AuthorList reference={queryReference} />
        </PageContent>
      </PageContainer>
      <AppFooter />
    </>
  );
};

AuthorsPage.displayName = "AuthorsPage";

export default withRelay(AuthorsPage, pageQuery, {
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  variablesFromContext(context) {
    const queryVariables = {
      applicationId: appData.application.scmp.entityId,
      first: 200,
      types: ["junior_reporter"],
    };
    if (context.query.char === "others")
      return {
        ...queryVariables,
        name: String.raw`^[\d|\W]`,
        nameOperator: "REGEX" as StringFilterOperator,
      };

    return {
      ...queryVariables,
      name: getLetterFromQueryString(context.query.char),
    };
  },
});
